/**
 * Service for activity feed business logic and data processing
 */
import type {
  ActivityFeedItemDto,
  ExpenseResponseDto,
} from '@splitfiesta/shared';

export interface ActivityFeedPage {
  activities: ActivityFeedItemDto[];
  pagination: {
    hasMore: boolean;
    nextCursor?: string;
    limit: number;
  };
}

export interface ProcessedActivity extends ActivityFeedItemDto {
  isOptimistic?: boolean;
  optimisticId?: string;
}

export interface ActivityInteraction {
  expenseId: string;
  type: 'comment' | 'reaction';
  data: unknown;
}

class ActivityFeedService {
  /**
   * Process and flatten paginated activity data
   */
  processPaginatedActivities(pages: ActivityFeedPage[]): ProcessedActivity[] {
    if (!pages || pages.length === 0) return [];
    return pages.flatMap(page => page.activities || []);
  }

  /**
   * Merge optimistic updates with actual data
   */
  mergeOptimisticActivity(
    currentActivities: ProcessedActivity[],
    optimisticActivity: ProcessedActivity
  ): ProcessedActivity[] {
    // Add optimistic activity at the beginning
    return [optimisticActivity, ...currentActivities];
  }

  /**
   * Remove optimistic activity after server confirmation
   */
  removeOptimisticActivity(
    activities: ProcessedActivity[],
    optimisticId: string
  ): ProcessedActivity[] {
    return activities.filter(
      activity => activity.optimisticId !== optimisticId
    );
  }

  /**
   * Calculate activity metrics
   */
  calculateActivityMetrics(activities: ActivityFeedItemDto[]) {
    const metrics = {
      totalActivities: activities.length,
      byType: {} as Record<string, number>,
      byGroup: {} as Record<string, number>,
      recentCount: 0,
    };

    const now = new Date();
    const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    activities.forEach(activity => {
      // Count by type
      const type = activity.type || 'unknown';
      metrics.byType[type] = (metrics.byType[type] || 0) + 1;

      // Count by group
      if (activity.groupId) {
        metrics.byGroup[activity.groupId] =
          (metrics.byGroup[activity.groupId] || 0) + 1;
      }

      // Count recent activities (last 24 hours)
      if (activity.timestamp && new Date(activity.timestamp) > dayAgo) {
        metrics.recentCount++;
      }
    });

    return metrics;
  }

  /**
   * Group activities by date for display
   */
  groupActivitiesByDate(
    activities: ActivityFeedItemDto[]
  ): Map<string, ActivityFeedItemDto[]> {
    const grouped = new Map<string, ActivityFeedItemDto[]>();

    activities.forEach(activity => {
      if (!activity.timestamp) return;

      const date = new Date(activity.timestamp);
      const dateKey = this.getDateGroupKey(date);

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(activity);
    });

    return grouped;
  }

  /**
   * Get date group key for grouping activities
   */
  private getDateGroupKey(date: Date): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const activityDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    if (activityDate.getTime() === today.getTime()) {
      return 'today';
    } else if (activityDate.getTime() === yesterday.getTime()) {
      return 'yesterday';
    } else if (
      activityDate > new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    ) {
      return 'this_week';
    } else if (
      activityDate > new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
    ) {
      return 'this_month';
    } else {
      return 'older';
    }
  }

  /**
   * Filter activities based on criteria
   */
  filterActivities(
    activities: ActivityFeedItemDto[],
    criteria: {
      type?: string;
      groupId?: string;
      userId?: string;
      startDate?: Date;
      endDate?: Date;
    }
  ): ActivityFeedItemDto[] {
    return activities.filter(activity => {
      if (criteria.type && activity.type !== criteria.type) {
        return false;
      }

      if (criteria.groupId && activity.groupId !== criteria.groupId) {
        return false;
      }

      if (criteria.userId && activity.userId !== criteria.userId) {
        return false;
      }

      if (criteria.startDate && activity.timestamp) {
        const activityDate = new Date(activity.timestamp);
        if (activityDate < criteria.startDate) return false;
      }

      if (criteria.endDate && activity.timestamp) {
        const activityDate = new Date(activity.timestamp);
        if (activityDate > criteria.endDate) return false;
      }

      return true;
    });
  }

  /**
   * Sort activities by various criteria
   */
  sortActivities(
    activities: ActivityFeedItemDto[],
    sortBy: 'date' | 'type' | 'group' = 'date',
    order: 'asc' | 'desc' = 'desc'
  ): ActivityFeedItemDto[] {
    const sorted = [...activities];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          if (a.timestamp && b.timestamp) {
            comparison =
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          }
          break;
        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;
        case 'group':
          comparison = (a.groupId || '').localeCompare(b.groupId || '');
          break;
      }

      return order === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }

  /**
   * Check if activity should trigger notification
   */
  shouldNotifyForActivity(
    activity: ActivityFeedItemDto,
    currentUserId: string
  ): boolean {
    // Don't notify for own activities
    if (activity.userId === currentUserId) return false;

    // Notify for mentions or direct interactions
    const notifiableTypes = [
      'expense_comment',
      'expense_reaction',
      'settlement_created',
      'payment_received',
    ];

    return notifiableTypes.includes(activity.type || '');
  }

  /**
   * Extract expense data from activity
   */
  extractExpenseFromActivity(
    activity: ActivityFeedItemDto
  ): ExpenseResponseDto | null {
    // Only extract if it's an expense activity
    if (activity.type !== 'expense') {
      return null;
    }

    // Map ActivityFeedItemDto to ExpenseResponseDto structure
    return {
      id: activity.id,
      title: activity.description,
      amount: activity.amount,
      currency: activity.currency,
      date: activity.timestamp,
      category: activity.category,
      subcategory: activity.subcategory,
      groupId: activity.groupId,
      createdAt: activity.timestamp,
      updatedAt: activity.timestamp,
      createdBy: activity.userId,
      payers:
        activity.paidBy?.map(p => ({
          userId: p.userId,
          userName: p.userName || 'Unknown',
          amount: p.amount,
        })) || [],
      splits:
        activity.splits?.map(s => ({
          userId: s.userId,
          userName: s.userName || 'Unknown',
          amount: s.amount,
          percentage: 0, // Not provided in activity feed
        })) || [],
      splitType: 'equal', // Default, not provided in activity feed
      notes: '',
      comments:
        activity.comments?.map(c => ({
          id: c.id,
          userId: c.userId,
          name: c.user,
          profileImage: c.profileImage,
          comment: c.text,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          isEdited: c.isEdited,
          replies: [],
        })) || [],
      reactions:
        activity.reactions?.map(r => ({
          id: `reaction-${r.emoji}-${activity.id}`,
          userId: '',
          userName: '',
          reaction: r.emoji,
          createdAt: activity.timestamp,
        })) || [],
      totalComments: activity.commentCount,
      hasMoreComments: activity.hasMoreComments,
    } as ExpenseResponseDto;
  }

  /**
   * Format activity for display
   */
  formatActivityDisplay(
    activity: ActivityFeedItemDto,
    t: (key: string, params?: Record<string, unknown>) => string
  ): {
    title: string;
    description: string;
    icon: string;
    color: string;
  } {
    const baseKey = `activity:types.${activity.type}`;

    // Extract params from metadata
    const params = this.extractActivityParams(activity);

    return {
      title: t(`${baseKey}.title`, params),
      description: t(`${baseKey}.description`, params),
      icon: this.getActivityIcon(activity.type || ''),
      color: this.getActivityColor(activity.type || ''),
    };
  }

  /**
   * Extract parameters for translation
   */
  private extractActivityParams(
    activity: ActivityFeedItemDto
  ): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    // Extract params from activity fields directly
    if (activity.user) params.userName = activity.user;
    if (activity.groupName) params.groupName = activity.groupName;
    if (activity.amount) params.amount = activity.amount;
    if (activity.currency) params.currency = activity.currency;
    if (activity.description) params.expenseTitle = activity.description;

    return params;
  }

  /**
   * Get icon for activity type
   */
  private getActivityIcon(type: string): string {
    const iconMap: Record<string, string> = {
      expense_created: 'receipt',
      expense_updated: 'edit',
      expense_deleted: 'trash',
      expense_comment: 'message',
      expense_reaction: 'heart',
      group_created: 'users',
      group_member_added: 'user-plus',
      group_member_removed: 'user-minus',
      settlement_created: 'check-circle',
      payment_received: 'dollar-sign',
    };

    return iconMap[type] || 'activity';
  }

  /**
   * Get color for activity type
   */
  private getActivityColor(type: string): string {
    const colorMap: Record<string, string> = {
      expense_created: 'blue',
      expense_updated: 'yellow',
      expense_deleted: 'red',
      expense_comment: 'purple',
      expense_reaction: 'pink',
      group_created: 'green',
      group_member_added: 'teal',
      group_member_removed: 'orange',
      settlement_created: 'emerald',
      payment_received: 'green',
    };

    return colorMap[type] || 'gray';
  }

  /**
   * Check if user can interact with activity
   */
  canInteractWithActivity(
    activity: ActivityFeedItemDto,
    _userId: string,
    userGroups: string[]
  ): boolean {
    // Check if user is part of the group
    if (activity.groupId && !userGroups.includes(activity.groupId)) {
      return false;
    }

    // Check activity type restrictions
    const interactableTypes = [
      'expense_created',
      'expense_updated',
      'settlement_created',
    ];

    return interactableTypes.includes(activity.type || '');
  }

  /**
   * Generate optimistic activity ID
   */
  generateOptimisticId(): string {
    return `optimistic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const activityFeedService = new ActivityFeedService();
